import { ButtonGradientPrimary, ButtonSecondary, ModalDialog } from '@amelio/ui-library';
import React from 'react';

import { TemplatePreviewList } from './TemplatePreviewList';

import type { ReviewQuestion } from '@/types/review/ReviewQuestion';

import { useAmelioTranslations } from '@/hooks';
import { useReplaceDynamicTags } from '@/hooks/useReplaceDynamicTags';
import { getLocalizedText, type LocalizedText } from '@/utils/localization';

type Props = {
    isOpen: boolean;
    onClose: () => void;
    onImport: () => void;
    questions: ReviewQuestion[];
    templateName: LocalizedText;
    templateDescription: string;
    isCreatingTemplate?: boolean;
    isLoading: boolean;
};

export const TemplateQuestionModal: React.FC<Props> = ({
    isOpen,
    onClose,
    onImport,
    questions,
    templateName,
    templateDescription,
    isCreatingTemplate,
    isLoading,
}) => {
    const { t } = useAmelioTranslations();
    const replaceDynamicTags = useReplaceDynamicTags();

    return (
        <ModalDialog
            isOpen={isOpen}
            onClose={onClose}
            closeAriaLabel={t('common.close')}
            title={replaceDynamicTags({ text: getLocalizedText(templateName) })}
            subtitle={templateDescription}
            headingLevel={4}
            size={'md'}
            backdropBlur
            dataTestId={'template-question-modal'}
            footer={
                <div className={'flex items-center justify-end gap-3'}>
                    <ButtonSecondary
                        dataTestId={'close-question-modal-button'}
                        onClick={onClose}
                    >
                        {t('common.close')}
                    </ButtonSecondary>

                    {questions.length > 0 && (
                        <ButtonGradientPrimary
                            onClick={onImport}
                            dataTestId={'import-template-button'}
                            loading={isCreatingTemplate}
                        >
                            {t(
                                'templateManagement.template.importTemplateQuestionModal.importTemplateButton',
                            )}
                        </ButtonGradientPrimary>
                    )}
                </div>
            }
        >
            <TemplatePreviewList
                heading={t(
                    'templateManagement.template.importTemplateQuestionModal.questionPreview',
                )}
                items={questions.map((question) =>
                    replaceDynamicTags({ text: getLocalizedText(question.label) }),
                )}
                emptyMessage={t(
                    'templateManagement.template.importTemplateQuestionModal.noQuestions',
                )}
                isLoading={isLoading}
            />
        </ModalDialog>
    );
};
